import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DollarSign, ChevronLeft } from 'lucide-react';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import Input from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import SpinnerButton from '@/components/common/SpinnerButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

import { OFFERING_CURRENCY } from '../../enums';

import { pricingAndBookingSchema } from '../../_validator';

const PricingAndBookingForm = ({
  initialValues,
  onHandleSubmit,
  onPreviousStep,
  submitting,
  submitButtonText = 'Create Offering'
}) => {
  const form = useForm({
    resolver: zodResolver(pricingAndBookingSchema),
    defaultValues: { ...initialValues }
  });

  const handleSubmit = async (values) => {
    await onHandleSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="space-y-4">
          <Input
            label="Price Per Client"
            name="pricePerClient"
            startIcon={<DollarSign />}
            type="number"
            placeholder={'25.00'}
            description="This is what you recieve per client. ProFound will tack on a small fee to cover processing costs and that final amount is the price the client will see."
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.keys(OFFERING_CURRENCY).map((key) => (
                      <SelectItem key={key} value={key}>
                        {OFFERING_CURRENCY[key]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="isGroup"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Group Offering</FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {form.watch('isGroup') && (
            <FormField
              control={form.control}
              name="maxGroupClients"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max Group Clients</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(Number.parseInt(e.target.value))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="bookingLeadTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Booking Lead Time (hours)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) =>
                      field.onChange(Number.parseInt(e.target.value))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex justify-between pt-8">
          <Button
            type="button"
            className="gap-1"
            onClick={onPreviousStep}
            variant="outline"
          >
            <ChevronLeft />
            Previous
          </Button>
          <SpinnerButton loading={submitting} type="submit">
            {submitButtonText}
          </SpinnerButton>
        </div>
      </form>
    </Form>
  );
};

export default PricingAndBookingForm;
